import { Terminal } from '@xterm/xterm'
import { FitAddon } from '@xterm/addon-fit'
import { ClipboardAddon } from '@xterm/addon-clipboard'
import type { IClipboardProvider } from '@xterm/addon-clipboard'
import { terminalSocketURL } from '../utilities/terminalSocket'

const routerApiBaseUrl =
  import.meta.env.VITE_ROUTER_API_URL?.replace(/\/$/, '') ?? 'https://router.nobgp.com'

type Status = 'connecting' | 'connected' | 'disconnected'

// OSC 52 lets a program on the node drive the clipboard. We allow the WRITE
// direction only — that is what `c to copy` in Claude Code (and any TUI that
// offers it) needs, and without the addon the escape sequence is silently
// dropped, so the copy appears to do nothing.
//
// readText stays shut deliberately. OSC 52 read lets a program running on the
// remote node ask for whatever the operator last copied — a password, a key —
// and receive it over the wire. Terminals here attach to arbitrary nodes,
// including ones shared with other people, so that direction is never worth
// the convenience. Returning '' answers the query with an empty selection
// rather than leaving the program waiting for a reply that never comes.
//
// Only the SYSTEM selection ('c') is honored, matching the addon's own
// BrowserClipboardProvider. OSC 52 also addresses the X11 PRIMARY selection
// ('p'), which vim and tmux sync on ORDINARY MOUSE SELECTION, not on an
// explicit copy — answering those would let a remote program overwrite
// whatever the operator had on their clipboard every time they dragged across
// a remote buffer. Write means "the user asked to copy", and only 'c' says so.
//
// Typed via the interface rather than by implementing a class, so
// ClipboardSelectionType (a const enum, which Vite's isolatedModules cannot
// value-import) is only ever contextually inferred.
const writeOnlyClipboard: IClipboardProvider = {
  readText: () => '',
  writeText: async (selection, text) => {
    if (selection !== 'c') return
    // The addon hands our promise straight to xterm's OSC handler, so a
    // rejection (tab not focused, Firefox outside a user gesture, permission
    // denied) surfaces as an uncaught error mid-parse. A failed copy is worth
    // a console line, not a broken write buffer.
    try {
      await navigator.clipboard.writeText(text)
    } catch (error) {
      console.warn('clipboard write failed', error)
    }
  },
}

type Theme = {
  background: string
  foreground: string
  cursor: string
  selectionBackground?: string
  selectionInactiveBackground?: string
}

type StatusListener = (status: Status) => void

export class Service {
  private terminal: Terminal
  private fitAddon: FitAddon
  private websocket: WebSocket | null = null
  private encoder = new TextEncoder()
  private decoder = new TextDecoder()
  private status: Status = 'connecting'
  private statusListeners: Set<StatusListener> = new Set()
  private resizeObserver: ResizeObserver | null = null
  private windowResizeHandler: (() => void) | null = null
  private attached = false

  constructor(theme: Theme) {
    this.terminal = new Terminal({
      cursorBlink: true,
      fontSize: 14,
      fontFamily: 'IBM Plex Mono, monospace',
      // A full-screen TUI (Claude Code, vim, tmux) turns on mouse reporting,
      // and from then on the terminal must forward drags to the program — so a
      // plain drag selects nothing, xterm writes nothing on copy, and the
      // browser falls back to copying one row out of xterm's hidden helper
      // textarea. That reads as "the clipboard truncated my URL" when in fact
      // nothing was ever selected. This turns on the escape hatch every native
      // terminal has (hold Option to select locally regardless of mouse mode);
      // xterm ships it off by default. Plain drag still reaches the program, so
      // the TUI's own mouse handling keeps working.
      macOptionClickForcesSelection: true,
      theme,
    })
    this.fitAddon = new FitAddon()
    this.terminal.loadAddon(this.fitAddon)
    this.terminal.loadAddon(new ClipboardAddon(undefined, writeOnlyClipboard))

    // Register terminal event handlers
    this.terminal.onData(data => {
      if (this.status !== 'connected') return
      if (data === '\f') {
        this.terminal.clear()
        return
      }
      if (!this.websocket || this.websocket.readyState !== WebSocket.OPEN) return
      this.websocket.send(this.encoder.encode(data))
    })

    this.terminal.onResize(() => {
      this.sendSize()
    })
  }

  attachToElement(element: HTMLElement, container?: HTMLElement) {
    if (this.attached) {
      this.detach()
    }

    if (this.terminal.element) {
      // Already opened — move the existing DOM node to the new parent
      element.appendChild(this.terminal.element)
    } else {
      this.terminal.open(element)
    }
    this.attached = true

    this.fit()
    this.scheduleFit()

    // Set up resize observers
    this.windowResizeHandler = () => this.fitAddon.fit()
    window.addEventListener('resize', this.windowResizeHandler)

    if (container) {
      this.resizeObserver = new ResizeObserver(() => this.fitAddon.fit())
      this.resizeObserver.observe(container)
    }
  }

  detach() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
      this.resizeObserver = null
    }

    if (this.windowResizeHandler) {
      window.removeEventListener('resize', this.windowResizeHandler)
      this.windowResizeHandler = null
    }

    this.attached = false
  }

  connect(serviceName: string, title?: string) {
    if (this.websocket && this.websocket.readyState === WebSocket.OPEN) return

    const url = terminalSocketURL(routerApiBaseUrl, serviceName)

    this.disconnect()
    this.setStatus('connecting')
    this.writeMessage(`Connecting to ${title || serviceName}...`)

    try {
      if (import.meta.env.MODE === 'development') {
        console.log('[Terminal] Connecting to:', url)
        console.log('[Terminal] Note: the router authenticates this with your app login cookie (nobgp_sid)')
      }

      const ws = new WebSocket(url)
      ws.binaryType = 'arraybuffer'
      this.websocket = ws

      ws.onopen = () => {
        this.setStatus('connected')
        this.writeMessage('Connected successfully!')
        this.writeSeparator()
        this.fit()
        this.sendSize()
        this.scheduleFit()
        this.terminal.focus()
      }

      ws.onclose = event => {
        const codeInfo = event.reason ? ` (${event.code}: ${event.reason})` : ` (${event.code})`
        if (this.status === 'connected') {
          this.terminal.writeln('')
          this.writeSeparator()
        }
        this.setStatus('disconnected')
        this.writeMessage(`Connection closed${codeInfo}`)
      }

      ws.onerror = () => {
        this.setStatus('disconnected')
        this.writeMessage('Connection error occurred')
      }

      ws.onmessage = event => {
        if (typeof event.data === 'string') {
          try {
            JSON.parse(event.data)
          } catch {
            this.terminal.write(event.data)
          }
          return
        }

        if (event.data instanceof Blob) {
          event.data.arrayBuffer().then(buffer => {
            this.terminal.write(this.decoder.decode(buffer))
          })
          return
        }

        if (event.data instanceof ArrayBuffer) {
          this.terminal.write(this.decoder.decode(event.data))
        }
      }
    } catch (error) {
      console.error('terminal connect error', error)
      this.setStatus('disconnected')
      this.writeMessage(`Error: ${(error as Error).message}`)
    }
  }

  disconnect() {
    if (!this.websocket) return
    try {
      this.websocket.close()
    } catch (err) {
      console.warn('terminal disconnect error', err)
    }
    this.websocket = null
  }

  dispose() {
    this.disconnect()
    this.detach()

    this.terminal.dispose()
    this.statusListeners.clear()
  }

  setTheme(theme: Theme) {
    this.terminal.options.theme = theme
  }

  focus() {
    this.terminal.focus()
  }

  fit() {
    this.fitAddon.fit()
  }

  scheduleFit() {
    requestAnimationFrame(() => this.fitAddon.fit())
    setTimeout(() => {
      this.fitAddon.fit()
      this.sendSize()
    }, 50)
  }

  getStatus(): Status {
    return this.status
  }

  onStatusChange(listener: StatusListener) {
    this.statusListeners.add(listener)
    return () => this.statusListeners.delete(listener)
  }

  private setStatus(status: Status) {
    this.status = status
    this.statusListeners.forEach(listener => listener(status))
  }

  private sendSize() {
    if (!this.websocket || this.websocket.readyState !== WebSocket.OPEN) return
    const size = {
      cols: Math.max(this.terminal.cols, 1),
      rows: Math.max(this.terminal.rows, 1),
    }

    this.websocket.send(
      JSON.stringify({
        type: 'resize',
        cols: size.cols,
        rows: size.rows,
      })
    )
  }

  private writeSeparator() {
    const width = Math.max(this.terminal.cols || 0, 1)
    this.terminal.writeln(`\x1b[2m${'─'.repeat(width)}\x1b[0m`)
  }

  private writeMessage(msg: string) {
    const time = new Date().toLocaleTimeString()
    this.terminal.writeln(`\x1b[3m\x1b[2m${time}: ${msg}\x1b[0m`)
  }
}
